import { makeStyles, mergeClasses, Tooltip, TooltipProps } from '@fluentui/react-components';
import React, { ReactNode, useRef, useState } from 'react';

const useStyles = makeStyles({
    truncated: {
        display: 'block',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        lineHeight: '1.5em',
    },
});

export interface IDefaultTooltipHostV9 extends Omit<TooltipProps, 'children' | 'visible' | 'onVisibleChange' | 'relationship'> {
    children: ReactNode;
    /** Class name for the container div that wraps the children, NOT the tooltip itself. Optional to override default truncation styles. */
    containerClassName?: string;
    /** Whether to always display the tooltip even when the text is not truncated. */
    alwaysDisplay?: boolean;
    tabIndex?: number;
    /**
     * Fluent tooltip relationship. Defaults to `inaccessible` for truncation/mirror tooltips
     * (content already present in children) so inactive tooltips are not mounted into the a11y tree.
     * Pass `description` or `label` when the tooltip adds information not already in the trigger.
     */
    relationship?: TooltipProps['relationship'];
}

export const DefaultTooltipHostV9: React.FC<IDefaultTooltipHostV9> = ({
    children,
    containerClassName,
    alwaysDisplay = false,
    tabIndex = -1,
    relationship = 'inaccessible',
    appearance = 'inverted',
    withArrow = true,
    ...restProps
}) => {
    const textRef = useRef<HTMLDivElement>(null);
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const styles = useStyles();

    const truncatedContent = (
        <div ref={textRef} className={mergeClasses(styles.truncated, containerClassName)} tabIndex={tabIndex} data-tooltip-host='true'>
            {children}
        </div>
    );
    if (!restProps.content) {
        return truncatedContent;
    }

    return (
        <Tooltip
            {...restProps}
            appearance={appearance}
            withArrow={withArrow}
            relationship={relationship}
            visible={tooltipVisible}
            onVisibleChange={(_ev, { visible }) => {
                if (visible && !alwaysDisplay && textRef.current) {
                    visible = textRef.current.scrollWidth > textRef.current.clientWidth;
                }
                setTooltipVisible(visible);
            }}
        >
            {truncatedContent}
        </Tooltip>
    );
};
